(() => {
  'use strict';

  // mm:ss, as in Safari's own controls ("00:03").
  const formatTime = (seconds) => {
    const total = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  if (typeof module === 'object' && module.exports) module.exports = { formatTime };
  if (typeof customElements === 'undefined' || customElements.get('unik-video-controls')) return;

  // Bar for a muted product video (snippets/unik-product-video-controls.liquid). Colorblock's
  // deferred-media inserts the <video> from its template on demand (poster click, active
  // slide, lightbox). Media events do not bubble, so the host listens in the capture phase
  // and the controls attach to the video on its first loadstart/play.
  class UnikVideoControls extends HTMLElement {
    connectedCallback() {
      this.host = this.closest('deferred-media');
      if (!this.host) return;
      this.toggle = this.querySelector('.unik-video-controls__toggle');
      this.seek = this.querySelector('[data-video-seek]');
      this.current = this.querySelector('[data-video-current]');
      this.duration = this.querySelector('[data-video-duration]');

      this.onMediaEvent = (event) => {
        if (event.target.nodeName === 'VIDEO') this.attach(event.target);
      };
      this.onToggle = () => {
        if (!this.video) return;
        if (this.video.paused || this.video.ended) {
          const playing = this.video.play();
          if (playing) playing.catch(() => {});
        } else {
          this.video.pause();
        }
      };
      this.onSeek = () => {
        if (!this.video || !Number.isFinite(this.video.duration)) return;
        this.video.currentTime = (Number(this.seek.value) / 100) * this.video.duration;
        this.render();
      };
      this.onVideoEvent = () => this.render();

      ['loadstart', 'play'].forEach((type) => this.host.addEventListener(type, this.onMediaEvent, true));
      this.toggle.addEventListener('click', this.onToggle);
      this.seek.addEventListener('input', this.onSeek);

      const video = this.host.querySelector(':scope > video');
      if (video) this.attach(video);
    }

    disconnectedCallback() {
      if (!this.host) return;
      ['loadstart', 'play'].forEach((type) => this.host.removeEventListener(type, this.onMediaEvent, true));
      this.toggle.removeEventListener('click', this.onToggle);
      this.seek.removeEventListener('input', this.onSeek);
      this.detach();
    }

    attach(video) {
      if (this.video === video) return;
      this.detach();
      this.video = video;
      UnikVideoControls.videoEvents.forEach((type) => video.addEventListener(type, this.onVideoEvent));
      video.addEventListener('click', this.onToggle);
      this.classList.add('is-ready');
      this.render();
    }

    detach() {
      if (!this.video) return;
      UnikVideoControls.videoEvents.forEach((type) => this.video.removeEventListener(type, this.onVideoEvent));
      this.video.removeEventListener('click', this.onToggle);
      this.video = null;
    }

    render() {
      const { video } = this;
      const playing = !video.paused && !video.ended;
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      const progress = duration ? Math.min(100, (video.currentTime / duration) * 100) : 0;
      const current = formatTime(video.currentTime);
      const total = formatTime(duration);

      this.classList.toggle('is-playing', playing);
      this.toggle.setAttribute('aria-label', playing ? this.toggle.dataset.pauseLabel : this.toggle.dataset.playLabel);
      this.current.textContent = current;
      this.duration.textContent = total;
      this.seek.value = String(progress);
      this.seek.style.setProperty('--unik-video-progress', `${progress}%`);
      this.seek.setAttribute('aria-valuetext', this.seek.dataset.positionLabel.replace('%current%', current).replace('%duration%', total));
    }
  }

  UnikVideoControls.videoEvents = ['play', 'pause', 'ended', 'timeupdate', 'durationchange', 'loadedmetadata', 'seeked'];

  customElements.define('unik-video-controls', UnikVideoControls);
})();
